import { useState } from "react"
import { Link } from "react-router-dom"
import {
  Arrow,
  ButtonDark,
  IMG,
  Img,
  PageHero,
  PostCard,
  Reveal,
  Section,
  FinalCta,
} from "../lib/shared"
import { useCollection } from "../lib/content"
import { Seo, breadcrumbSchema, itemListSchema } from "../lib/seo"

export default function Insights() {
  const { items } = useCollection("posts")
  const published = items.filter((p) => p.status === "Published")

  /* Topics come from the posts themselves, so a filter never offers a
     category with nothing in it. With fewer than two there is nothing to
     choose between, and the row is left out. */
  const topics = [...new Set(published.map((p) => p.category).filter(Boolean))]
  const [topic, setTopic] = useState<string | null>(null)
  const shown = topic ? published.filter((p) => p.category === topic) : published
  const [featured, ...rest] = shown

  return (
    <>
      <Seo
        title="Insights on software, automation and brand"
        description="Practical thinking on software, AI automation and brand, written for the people making the decisions rather than the people writing the code."
        jsonLd={[
          itemListSchema(
            published.map((p) => ({
              name: p.title,
              path: `/insights/${p.slug}`,
            })),
            "Sableboxx insights",
          ),
          breadcrumbSchema([{ name: "Insights", path: "/insights" }]),
        ]}
      />
      <PageHero
        eyebrow="Insights"
        title="Notes on building better businesses"
        intro="Practical thinking on software, automation and brand, written for the people making the decisions rather than the people writing the code."
        image={IMG.insightsHero}
      />

      <Section tone="paper">
        {published.length === 0 ? (
          <Reveal>
            <div className="rounded-2xl border border-neutral-200 bg-mist px-8 py-16 lg:text-center">
              <h2 className="text-[24px] font-bold tracking-[-0.02em]">
                Our first articles are on the way
              </h2>
              <p className="mt-3 max-w-md lg:mx-auto text-[16px] leading-[1.7] text-neutral-600">
                Until then, if you have a question about software, automation
                or your brand, ask us directly. We&rsquo;re happy to help.
              </p>
              <div className="mt-8 flex lg:justify-center">
                <ButtonDark to="/contact">Ask a question</ButtonDark>
              </div>
            </div>
          </Reveal>
        ) : (
          <>
            {topics.length > 1 && (
              <Reveal>
                <div
                  className="mb-10 flex flex-wrap gap-2.5"
                  role="group"
                  aria-label="Filter articles by topic"
                >
                  {[null, ...topics].map((t) => (
                    <button
                      key={t ?? "all"}
                      type="button"
                      onClick={() => setTopic(t)}
                      aria-pressed={topic === t}
                      className={`rounded-full border px-4 py-2 text-[13.5px] font-medium transition-colors ${
                        topic === t
                          ? "border-ink bg-ink text-white"
                          : "border-neutral-300 text-neutral-600 hover:border-ink hover:text-ink"
                      }`}
                    >
                      {t ?? "All"}
                    </button>
                  ))}
                </div>
              </Reveal>
            )}

            {featured && (
              <Reveal>
                <Link
                  to={`/insights/${featured.slug}`}
                  className="group grid grid-cols-1 overflow-hidden rounded-2xl border border-neutral-200 bg-white transition-shadow duration-500 hover:shadow-[0_30px_60px_-38px_rgba(11,16,23,0.35)] lg:grid-cols-2"
                >
                  <Img
                    src={featured.cover}
                    label={featured.category}
                    alt={featured.title}
                    className="aspect-[16/11] lg:aspect-auto lg:h-full [&_img]:group-hover:scale-[1.04]"
                  />
                  <div className="flex flex-col justify-center p-8 lg:p-14">
                    <div className="flex items-center gap-3 text-[11.5px] font-semibold uppercase tracking-[0.14em]">
                      <span className="text-accent">
                        {featured.category || "Latest"}
                      </span>
                      {featured.readTime && (
                        <>
                          <span className="text-neutral-300">·</span>
                          <span className="text-neutral-500">
                            {featured.readTime}
                          </span>
                        </>
                      )}
                    </div>
                    <h2 className="mt-5 text-[clamp(1.8rem,2.8vw,2.4rem)] font-black leading-[1.08] tracking-[-0.02em]">
                      {featured.title}
                    </h2>
                    <p className="mt-4 max-w-md text-[16px] leading-[1.7] text-neutral-600">
                      {featured.excerpt}
                    </p>
                    <span className="mt-7 inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-ink">
                      Read article
                      <Arrow className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            )}

            {rest.length > 0 && (
              <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
                {rest.map((a, i) => (
                  <Reveal key={a.id} delay={(i % 3) * 90}>
                    <PostCard post={a} />
                  </Reveal>
                ))}
              </div>
            )}
          </>
        )}
      </Section>

      <FinalCta />
    </>
  )
}
